import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { PlusCircle, Tag, Calendar, Briefcase, Save, AlertCircle, Clock, Building2 } from 'lucide-react';
import { EmptyState } from '../UI/Card';
import type { DiscountCategory, EventCategory, JobType, JobCategory, UserType } from '../../types/database';

type PostType = 'discount' | 'event' | 'job';

interface VendorStatus {
  exists: boolean;
  status: string | null;
  business_name: string | null;
  rejection_reason: string | null;
}

export function VendorPost() {
  const { profile } = useAuth();
  const [postType, setPostType] = useState<PostType>('discount');
  const [loading, setLoading] = useState(false);
  const [checkingVendor, setCheckingVendor] = useState(true);
  const [vendorStatus, setVendorStatus] = useState<VendorStatus>({ exists: false, status: null, business_name: null, rejection_reason: null });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [discountForm, setDiscountForm] = useState({
    title: '',
    description: '',
    discount_amount: '',
    category: 'food' as DiscountCategory,
    valid_for: ['student', 'professional', 'resident', 'visitor'] as UserType[],
    image_url: '',
    terms: '',
    expiry_date: '',
  });

  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    category: 'networking' as EventCategory,
    location: '',
    event_date: '',
    image_url: '',
    capacity: '',
    price: '',
    registration_required: false,
  });

  const [jobForm, setJobForm] = useState({
    title: '',
    description: '',
    company_name: '',
    job_type: 'full-time' as JobType,
    category: 'tech' as JobCategory,
    location: '',
    salary_range: '',
    requirements: '',
    application_url: '',
  });

  useEffect(() => {
    checkVendorStatus();
  }, [profile]);

  const checkVendorStatus = async () => {
    if (!profile?.id) return;

    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('status, business_name, rejection_reason')
        .eq('id', profile.id)
        .maybeSingle();

      if (error) throw error;

      setVendorStatus({
        exists: !!data,
        status: data?.status || null,
        business_name: data?.business_name || null,
        rejection_reason: data?.rejection_reason || null,
      });
    } catch (err) {
      console.error('Error checking vendor status:', err);
    } finally {
      setCheckingVendor(false);
    }
  };

  const handleDiscountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const { error } = await supabase.from('discounts').insert({
        vendor_id: profile!.id,
        ...discountForm,
        expiry_date: discountForm.expiry_date || null,
      });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Discount posted successfully!' });
      setDiscountForm({
        title: '',
        description: '',
        discount_amount: '',
        category: 'food',
        valid_for: ['student', 'professional', 'resident', 'visitor'],
        image_url: '',
        terms: '',
        expiry_date: '',
      });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to post discount' });
      console.error('Error posting discount:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEventSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const { error } = await supabase.from('events').insert({
        organizer_id: profile!.id,
        ...eventForm,
        capacity: eventForm.capacity ? parseInt(eventForm.capacity) : null,
        price: eventForm.price ? parseFloat(eventForm.price) : 0,
      });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Event posted successfully!' });
      setEventForm({
        title: '',
        description: '',
        category: 'networking',
        location: '',
        event_date: '',
        image_url: '',
        capacity: '',
        price: '',
        registration_required: false,
      });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to post event' });
      console.error('Error posting event:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJobSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const { error } = await supabase.from('jobs').insert({
        employer_id: profile!.id,
        ...jobForm,
      });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Job posted successfully!' });
      setJobForm({
        title: '',
        description: '',
        company_name: '',
        job_type: 'full-time',
        category: 'tech',
        location: '',
        salary_range: '',
        requirements: '',
        application_url: '',
      });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to post job' });
      console.error('Error posting job:', error);
    } finally {
      setLoading(false);
    }
  };

  if (profile?.user_type !== 'vendor') {
    return (
      <EmptyState
        icon={<PlusCircle className="w-full h-full" />}
        title="Vendor Access Only"
        description="You need to be a vendor to access this page"
      />
    );
  }

  if (checkingVendor) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Checking vendor status...</p>
        </div>
      </div>
    );
  }

  if (!vendorStatus.exists) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full mb-4">
              <Building2 className="text-blue-600 dark:text-blue-400" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Setup Your Business Profile
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Before you can post content, you need to complete your business profile and get approved by our admin team.
            </p>
          </div>
          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                1
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Complete Business Profile</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Fill in your business details including name, category, and contact information
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                2
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Submit for Approval</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Our admin team will review your business profile
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                3
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Start Posting</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Once approved, you can post discounts, events, and job opportunities
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={() => window.location.href = '#business-profile'}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Setup Business Profile
          </button>
        </div>
      </div>
    );
  }

  if (vendorStatus.status === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 dark:bg-yellow-900/20 rounded-full mb-4">
              <Clock className="text-yellow-600 dark:text-yellow-400" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Profile Pending Approval
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Your business profile is currently under review by our admin team. You'll be able to post content once your profile is approved.
            </p>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800 dark:text-blue-400">
                <strong>Business Name:</strong> {vendorStatus.business_name}
              </p>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              We'll notify you once your profile has been reviewed. This usually takes 1-2 business days.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (vendorStatus.status === 'rejected') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full mb-4">
              <AlertCircle className="text-red-600 dark:text-red-400" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Profile Not Approved
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Unfortunately, your business profile was not approved. Please review the reason below and update your profile.
            </p>
            {vendorStatus.rejection_reason && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-red-800 dark:text-red-400 mb-1">
                  Rejection Reason:
                </p>
                <p className="text-sm text-red-700 dark:text-red-300">
                  {vendorStatus.rejection_reason}
                </p>
              </div>
            )}
            <button
              onClick={() => window.location.href = '#business-profile'}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Update Business Profile
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="glass-card bg-gradient-to-r from-indigo-600 to-blue-500 text-white py-12 mx-4 mt-4 mb-6 shadow-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-4">
            <PlusCircle className="w-8 h-8" />
            <h1 className="text-3xl md:text-4xl font-bold">Create New Post</h1>
          </div>
          <p className="text-indigo-100 text-lg">
            Share discounts, events, and job opportunities with the community
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message && (
          <div
            className={`glass-card mb-6 p-4 ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                : 'bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="glass-card p-6 mb-6">
          <div className="flex space-x-2">
            <button
              onClick={() => setPostType('discount')}
              className={`glass-button flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                postType === 'discount'
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                  : 'hover:scale-105'
              }`}
            >
              <Tag className="w-5 h-5" />
              <span>Discount</span>
            </button>
            <button
              onClick={() => setPostType('event')}
              className={`glass-button flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                postType === 'event'
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                  : 'hover:scale-105'
              }`}
            >
              <Calendar className="w-5 h-5" />
              <span>Event</span>
            </button>
            <button
              onClick={() => setPostType('job')}
              className={`glass-button flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                postType === 'job'
                  ? 'bg-gradient-to-r from-indigo-600 to-blue-500 text-white'
                  : 'hover:scale-105'
              }`}
            >
              <Briefcase className="w-5 h-5" />
              <span>Job</span>
            </button>
          </div>
        </div>

        <div className="glass-card p-8">
          {postType === 'discount' && (
            <form onSubmit={handleDiscountSubmit} className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Post a Discount</h2>

              <div>
                <label htmlFor="discount_title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Discount Title *
                </label>
                <input
                  id="discount_title"
                  type="text"
                  required
                  value={discountForm.title}
                  onChange={(e) => setDiscountForm({ ...discountForm, title: e.target.value })}
                  className="glass-input w-full"
                  placeholder="e.g., 20% off all menu items"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="discount_amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Discount Amount *
                  </label>
                  <input
                    id="discount_amount"
                    type="text"
                    required
                    value={discountForm.discount_amount}
                    onChange={(e) => setDiscountForm({ ...discountForm, discount_amount: e.target.value })}
                    className="glass-input w-full"
                    placeholder="e.g., 20% off, $10 off"
                  />
                </div>

                <div>
                  <label htmlFor="discount_category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category *
                  </label>
                  <select
                    id="discount_category"
                    value={discountForm.category}
                    onChange={(e) => setDiscountForm({ ...discountForm, category: e.target.value as DiscountCategory })}
                    className="glass-input w-full"
                  >
                    <option value="food">Food & Dining</option>
                    <option value="retail">Retail</option>
                    <option value="services">Services</option>
                    <option value="entertainment">Entertainment</option>
                    <option value="education">Education</option>
                    <option value="health">Health & Wellness</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="discount_description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description *
                </label>
                <textarea
                  id="discount_description"
                  required
                  rows={4}
                  value={discountForm.description}
                  onChange={(e) => setDiscountForm({ ...discountForm, description: e.target.value })}
                  className="glass-input w-full resize-none"
                  placeholder="Describe your discount offer..."
                />
              </div>

              <div>
                <label htmlFor="discount_terms" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Terms & Conditions
                </label>
                <textarea
                  id="discount_terms"
                  rows={3}
                  value={discountForm.terms}
                  onChange={(e) => setDiscountForm({ ...discountForm, terms: e.target.value })}
                  className="glass-input w-full resize-none"
                  placeholder="Any restrictions or conditions..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="discount_image" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Image URL
                  </label>
                  <input
                    id="discount_image"
                    type="url"
                    value={discountForm.image_url}
                    onChange={(e) => setDiscountForm({ ...discountForm, image_url: e.target.value })}
                    className="glass-input w-full"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div>
                  <label htmlFor="discount_expiry" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Expiry Date
                  </label>
                  <input
                    id="discount_expiry"
                    type="date"
                    value={discountForm.expiry_date}
                    onChange={(e) => setDiscountForm({ ...discountForm, expiry_date: e.target.value })}
                    className="glass-input w-full"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="glass-button flex items-center justify-center space-x-2 w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 px-6 rounded-lg font-medium hover:from-orange-600 hover:to-red-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                <span>{loading ? 'Posting...' : 'Post Discount'}</span>
              </button>
            </form>
          )}

          {postType === 'event' && (
            <form onSubmit={handleEventSubmit} className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Post an Event</h2>

              <div>
                <label htmlFor="event_title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Event Title *
                </label>
                <input
                  id="event_title"
                  type="text"
                  required
                  value={eventForm.title}
                  onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  className="glass-input w-full"
                  placeholder="e.g., Community Networking Night"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="event_category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category *
                  </label>
                  <select
                    id="event_category"
                    value={eventForm.category}
                    onChange={(e) => setEventForm({ ...eventForm, category: e.target.value as EventCategory })}
                    className="glass-input w-full"
                  >
                    <option value="networking">Networking</option>
                    <option value="workshop">Workshop</option>
                    <option value="social">Social</option>
                    <option value="sports">Sports</option>
                    <option value="cultural">Cultural</option>
                    <option value="business">Business</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="event_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Event Date & Time *
                  </label>
                  <input
                    id="event_date"
                    type="datetime-local"
                    required
                    value={eventForm.event_date}
                    onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
                    className="glass-input w-full"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="event_location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Location *
                </label>
                <input
                  id="event_location"
                  type="text"
                  required
                  value={eventForm.location}
                  onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                  className="glass-input w-full"
                  placeholder="e.g., Community Center, 123 Main St"
                />
              </div>

              <div>
                <label htmlFor="event_description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description *
                </label>
                <textarea
                  id="event_description"
                  required
                  rows={4}
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  className="glass-input w-full resize-none"
                  placeholder="Describe your event..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label htmlFor="event_capacity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Max Capacity
                  </label>
                  <input
                    id="event_capacity"
                    type="number"
                    value={eventForm.capacity}
                    onChange={(e) => setEventForm({ ...eventForm, capacity: e.target.value })}
                    className="glass-input w-full"
                    placeholder="50"
                  />
                </div>

                <div>
                  <label htmlFor="event_price" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Price
                  </label>
                  <input
                    id="event_price"
                    type="number"
                    step="0.01"
                    value={eventForm.price}
                    onChange={(e) => setEventForm({ ...eventForm, price: e.target.value })}
                    className="glass-input w-full"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label htmlFor="event_image" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Image URL
                  </label>
                  <input
                    id="event_image"
                    type="url"
                    value={eventForm.image_url}
                    onChange={(e) => setEventForm({ ...eventForm, image_url: e.target.value })}
                    className="glass-input w-full"
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  id="event_registration"
                  type="checkbox"
                  checked={eventForm.registration_required}
                  onChange={(e) => setEventForm({ ...eventForm, registration_required: e.target.checked })}
                  className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <label htmlFor="event_registration" className="ml-3 text-sm font-medium text-gray-700">
                  Registration required
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="glass-button flex items-center justify-center space-x-2 w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 px-6 rounded-lg font-medium hover:from-green-600 hover:to-emerald-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                <span>{loading ? 'Posting...' : 'Post Event'}</span>
              </button>
            </form>
          )}

          {postType === 'job' && (
            <form onSubmit={handleJobSubmit} className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Post a Job</h2>

              <div>
                <label htmlFor="job_title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Job Title *
                </label>
                <input
                  id="job_title"
                  type="text"
                  required
                  value={jobForm.title}
                  onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })}
                  className="glass-input w-full"
                  placeholder="e.g., Software Engineer"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="job_company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Company Name *
                  </label>
                  <input
                    id="job_company"
                    type="text"
                    required
                    value={jobForm.company_name}
                    onChange={(e) => setJobForm({ ...jobForm, company_name: e.target.value })}
                    className="glass-input w-full"
                    placeholder="Your Company"
                  />
                </div>

                <div>
                  <label htmlFor="job_location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Location *
                  </label>
                  <input
                    id="job_location"
                    type="text"
                    required
                    value={jobForm.location}
                    onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })}
                    className="glass-input w-full"
                    placeholder="City, State or Remote"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="job_type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Job Type *
                  </label>
                  <select
                    id="job_type"
                    value={jobForm.job_type}
                    onChange={(e) => setJobForm({ ...jobForm, job_type: e.target.value as JobType })}
                    className="glass-input w-full"
                  >
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                    <option value="volunteer">Volunteer</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="job_category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category *
                  </label>
                  <select
                    id="job_category"
                    value={jobForm.category}
                    onChange={(e) => setJobForm({ ...jobForm, category: e.target.value as JobCategory })}
                    className="glass-input w-full"
                  >
                    <option value="tech">Technology</option>
                    <option value="hospitality">Hospitality</option>
                    <option value="retail">Retail</option>
                    <option value="education">Education</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="finance">Finance</option>
                    <option value="marketing">Marketing</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="job_description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Job Description *
                </label>
                <textarea
                  id="job_description"
                  required
                  rows={4}
                  value={jobForm.description}
                  onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                  className="glass-input w-full resize-none"
                  placeholder="Describe the role and responsibilities..."
                />
              </div>

              <div>
                <label htmlFor="job_requirements" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Requirements
                </label>
                <textarea
                  id="job_requirements"
                  rows={3}
                  value={jobForm.requirements}
                  onChange={(e) => setJobForm({ ...jobForm, requirements: e.target.value })}
                  className="glass-input w-full resize-none"
                  placeholder="Skills and qualifications needed..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="job_salary" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Salary Range
                  </label>
                  <input
                    id="job_salary"
                    type="text"
                    value={jobForm.salary_range}
                    onChange={(e) => setJobForm({ ...jobForm, salary_range: e.target.value })}
                    className="glass-input w-full"
                    placeholder="e.g., $60k - $80k"
                  />
                </div>

                <div>
                  <label htmlFor="job_application" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Application URL
                  </label>
                  <input
                    id="job_application"
                    type="url"
                    value={jobForm.application_url}
                    onChange={(e) => setJobForm({ ...jobForm, application_url: e.target.value })}
                    className="glass-input w-full"
                    placeholder="https://yourcompany.com/careers"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="glass-button flex items-center justify-center space-x-2 w-full bg-gradient-to-r from-indigo-600 to-blue-500 text-white py-3 px-6 rounded-lg font-medium hover:from-indigo-700 hover:to-blue-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                <span>{loading ? 'Posting...' : 'Post Job'}</span>
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
